import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { User } from '@/types';
import AppInput from '@/components/AppInput';
import AppBox from '@/components/AppBox';
import AppText from '@/components/AppText';

interface AppAutocompleteProps {
  options: User[];
  value: User | null;
  onChange: (value: User | null) => void;
  loading?: boolean;
  placeholder?: string;
  className?: string;
}

const AppAutocomplete: React.FC<AppAutocompleteProps> = ({
  options,
  value,
  onChange,
  loading = false,
  placeholder = 'Search...',
  className
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const filteredOptions = options
    .filter(option => 
      option.name?.toLowerCase().includes(search.toLowerCase()) ||
      option.email?.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={wrapperRef} className={clsx('relative', className)}>
      <AppInput
        value={value ? `${value.name} (${value.email})` : search}
        onChange={(newValue) => {
          if (!value) {
            setSearch(newValue);
          }
          setIsOpen(true);
        }}
        placeholder={placeholder}
        onFocus={() => setIsOpen(true)}
      />

      {isOpen && (
        <AppBox 
          column 
          className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {filteredOptions.map((option) => (
            <AppBox
              key={option.id}
              className={clsx(
                'px-4 py-2 cursor-pointer hover:bg-gray-100',
                value?.id === option.id && 'bg-gray-100'
              )}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
                setSearch('');
              }}
            >
              <AppText>{option.name} ({option.email})</AppText>
            </AppBox>
          ))}
          {filteredOptions.length === 0 && (
            <AppBox className="px-4 py-2">
              <AppText color="disabled">No users found</AppText>
            </AppBox>
          )}
        </AppBox>
      )}
    </div>
  );
};

export default AppAutocomplete; 